import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import cx from 'classnames';
import { escapeRegExp } from 'lodash';
import PropTypes from 'prop-types';

import { IconChart_24 } from 'src/assets/icons/24x24/IconChart_24';
import { IconDownload_24 } from 'src/assets/icons/24x24/IconDownload_24';
import { IconEmail24 } from 'src/assets/icons/24x24/IconEmail24';
import { IconHelpCircle24 } from 'src/assets/icons/24x24/IconHelpCircle24';
import { IconLogOut24 } from 'src/assets/icons/24x24/IconLogOut24';
import { IconSettings24 } from 'src/assets/icons/24x24/IconSettings24';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { SearchMenuItem } from 'src/components/SearchMenuItem';
import { UserAvatar } from 'src/components/UserAvatar';
import { SUPPORT_URL, URL_HELP_CENTER } from 'src/const';
import { exportUserReport } from 'src/graphql/reports/exportUserReport';
import { useCurrentCustomerId } from 'src/store/customer';

import CustomerMenuItem from '../CustomerMenuItem';

import useStyles from './useStyles';

const moveSelectedUp = (customers, selectedId) => {
  if (!selectedId || customers.length <= 1) {
    return customers;
  }

  const selectedCustomer = customers.find((c) => String(c.id) === String(selectedId));
  if (!selectedCustomer) {
    return customers;
  }

  const filtered = customers.filter((c) => String(c.id) !== String(selectedId));
  return [selectedCustomer, ...filtered];
};

const filterCustomers = (customers, search) => {
  const regex = RegExp(escapeRegExp(search), 'i');
  return customers.filter((c) => regex.test(c.name));
};

export const ProfileMenu = ({
  classes,
  open,
  anchor,
  onClose,
  customers,
  selectedCustomerId,
  onCustomerClick,
  onSettingsClick,
  onUsageDashboardClick,
  showUsageDashboard,
  showExportOnly,
  onLogoutClick,
  onEditClick,
  disabledSettings,
  name,
  email,
}) => {
  const styles = useStyles({ classes });
  const customerId = useCurrentCustomerId();
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportClick = useCallback(async () => {
    if (!customerId || exportLoading) return;
    setExportLoading(true);
    try {
      const result = await exportUserReport({ customerId });
      if (result?.content && result?.filename) {
        const byteCharacters = atob(result.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } finally {
      setExportLoading(false);
    }
  }, [customerId, exportLoading]);

  const [search, setSearch] = useState('');
  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  const reorderedCustomers = useMemo(
    () => moveSelectedUp(customers, selectedCustomerId),
    [customers, selectedCustomerId]
  );

  const filteredCustomers = useMemo(() => {
    if (search) {
      return filterCustomers(reorderedCustomers, search);
    }
    return reorderedCustomers;
  }, [search, reorderedCustomers]);

  const customersPresent = customers.length > 1;
  const searchVisible = customers.length > 10;

  return (
    <Menu
      classes={{ paper: styles.paper }}
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      getContentAnchorEl={null}
      data-testid="profileMenu"
    >
      <div className={styles.userInfo}>
        <UserAvatar name={name} />
        <div className={styles.userNameContainer}>
          <div className={styles.nameContainer}>
            <Typography className={styles.name}>{name}</Typography>
            <IconButton
              className={styles.editIcon}
              data-testid="profileMenuEditUserItem"
              onClick={onEditClick}
            >
              <EditIcon />
            </IconButton>
          </div>
          <Typography
            className={styles.email}
            data-testid="profileMenuUserEmail"
          >
            {email}
          </Typography>
        </div>
      </div>
      {customersPresent && (
        <div
          className={styles.customerGroup}
          data-testid="profileMenuCustomItems"
        >
          <Typography className={styles.customerHeader}>Change account</Typography>
          {searchVisible && (
            <SearchMenuItem
              className={styles.searchItem}
              value={search}
              onChange={setSearch}
            />
          )}
          <div className={styles.customerItems}>
            {filteredCustomers.map((customer) => (
              <CustomerMenuItem
                classes={{ root: styles.customerItem }}
                key={customer.id}
                customer={customer}
                onClick={onCustomerClick}
                selected={String(customer.id) === String(selectedCustomerId)}
              />
            ))}
            {filteredCustomers.length === 0 && (
              <MenuItem
                className={cx(styles.menuItem, styles.noResults)}
                disabled
              >
                <ListItemText
                  classes={{
                    primary: styles.itemText,
                  }}
                  primary="No results"
                />
              </MenuItem>
            )}
          </div>
        </div>
      )}

      <div className={styles.settings}>
        <MenuItem
          className={styles.menuItem}
          onClick={onSettingsClick}
          disabled={disabledSettings}
          data-testid="profileMenuSettingsItem"
        >
          <IconSettings24 className={styles.itemIcon} />
          <ListItemText
            classes={{
              primary: styles.itemText,
            }}
            primary="Account settings"
          />
        </MenuItem>

        {showUsageDashboard && (
          <MenuItem
            className={styles.menuItem}
            onClick={onUsageDashboardClick}
            data-testid="profileMenuUsageDashboardItem"
          >
            <IconChart_24 className={styles.itemIcon} />
            <ListItemText
              classes={{
                primary: styles.itemText,
              }}
              primary="Usage dashboard"
            />
          </MenuItem>
        )}

        {showExportOnly && (
          <MenuItem
            className={styles.menuItem}
            onClick={handleExportClick}
            disabled={exportLoading}
            data-testid="profileMenuExportUsageReportItem"
          >
            {exportLoading ? (
              <CircularProgress
                size={24}
                className={styles.itemIcon}
              />
            ) : (
              <IconDownload_24 className={styles.itemIcon} />
            )}
            <ListItemText
              classes={{
                primary: styles.itemText,
              }}
              primary="Export usage report"
            />
          </MenuItem>
        )}

        <MenuItem
          className={styles.menuItem}
          component="a"
          href={URL_HELP_CENTER}
          target="_blank"
          data-testid="profileMenuHelpItem"
        >
          <IconHelpCircle24 className={styles.itemIcon} />
          <ListItemText
            classes={{
              primary: styles.itemText,
            }}
            primary="Help center"
          />
        </MenuItem>

        <MenuItem
          className={styles.menuItem}
          component="a"
          href={SUPPORT_URL}
          target="_blank"
          data-testid="profileMenuContactItem"
        >
          <IconEmail24 className={styles.itemIcon} />
          <ListItemText
            classes={{
              primary: styles.itemText,
            }}
            primary="Contact us"
          />
        </MenuItem>

        <MenuItem
          className={styles.menuItem}
          onClick={onLogoutClick}
          data-testid="profileMenuLogoutItem"
        >
          <IconLogOut24 className={styles.itemIcon} />
          <ListItemText
            classes={{
              primary: styles.itemText,
            }}
            primary="Log out"
          />
        </MenuItem>
      </div>
    </Menu>
  );
};

ProfileMenu.propTypes = {
  classes: PropTypes.shape(),
  anchor: PropTypes.instanceOf(Element),
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  customers: PropTypes.arrayOf(PropTypes.shape({})),
  selectedCustomerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCustomerClick: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  onUsageDashboardClick: PropTypes.func.isRequired,
  showUsageDashboard: PropTypes.bool,
  showExportOnly: PropTypes.bool,
  onLogoutClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  disabledSettings: PropTypes.bool,
  name: PropTypes.string,
  email: PropTypes.string.isRequired,
};

ProfileMenu.defaultProps = {
  classes: null,
  anchor: null,
  open: false,
  customers: [],
  selectedCustomerId: null,
  disabledSettings: false,
  name: '',
};
